import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Stack, TextField, Button, Alert, CircularProgress, Paper, Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";
import { mediaUrl } from "../../utils/media";
import { CATEGORIES, catLabel } from "../../utils/categories";
import { PLATFORM_DOMAIN } from "../../utils/host";

export default function SalonProfileEditor() {
  const { t } = useTranslation();
  const { activeWorkspaceId, workspaceRole, activeWorkspace } = useAuth();
  const isOwner = workspaceRole === "owner";
  const salonUrl = activeWorkspace?.slug ? `${activeWorkspace.slug}.${PLATFORM_DOMAIN}` : "";

  const [slug, setSlug] = useState("");
  const [slugSaved, setSlugSaved] = useState(""); // поточний збережений (для порівняння/превʼю)
  const [slugError, setSlugError] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const [coverUrl, setCoverUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError("");
    try {
      const { profile } = await api.getWorkspaceProfile(activeWorkspaceId);
      setSlug(profile.slug || "");
      setSlugSaved(profile.slug || "");
      setCity(profile.city || "");
      setAddress(profile.address || "");
      setDescription(profile.description || "");
      setCategories(profile.categories || []);
      setCoverUrl(profile.coverUrl || "");
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  const toggleCat = (slug) =>
    setCategories((prev) => (prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug]));

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { url } = await api.uploadWorkspaceImage(activeWorkspaceId, file);
      setCoverUrl(url);
    } catch (er) {
      setError(er.message);
    }
    setUploading(false);
  };

  const save = async () => {
    setError("");
    setOk("");
    setSaving(true);
    setSlugError("");
    try {
      const { profile } = await api.updateWorkspaceProfile(activeWorkspaceId, {
        slug, city, address, description, categories, coverUrl,
      });
      setOk(t("salonProfile.saved"));
      if (profile?.slug && profile.slug !== slugSaved) {
        // Піддомен змінився — перезавантажуємось, щоб усі адреси/контекст оновились
        window.location.reload();
        return;
      }
    } catch (e) {
      if (e.field === "slug" || /піддомен|subdomain|poddomen/i.test(e.message || "")) setSlugError(e.message);
      else setError(e.message);
    }
    setSaving(false);
  };

  if (!activeWorkspaceId) {
    return <Box sx={{ py: 4 }}><Typography color="text.secondary">{t("salonProfile.noWorkspace")}</Typography></Box>;
  }

  return (
    <Box sx={{ py: 2, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{t("salonProfile.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("salonProfile.subtitle")}</Typography>

      {salonUrl && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          {t("salonProfile.publicAddress")}:{" "}
          <b>{salonUrl}</b>
          <Box component="span" sx={{ color: "text.secondary" }}> · {t("salonProfile.orPath", { path: `/w/${activeWorkspace.slug}` })}</Box>
        </Alert>
      )}
      {!isOwner && <Alert severity="info" sx={{ mb: 2 }}>{t("salonProfile.ownerOnly")}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk("")}>{ok}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={3}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t("salonProfile.subdomain")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
              {t("salonProfile.subdomainHint")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <TextField
                value={slug}
                onChange={(e) => {
                  // одразу нормалізуємо ввід: нижній регістр, без пробілів
                  setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"));
                  setSlugError("");
                }}
                disabled={!isOwner}
                size="small"
                error={!!slugError}
                inputProps={{ maxLength: 30, style: { textAlign: "right" } }}
                sx={{ width: 260 }}
              />
              <Typography variant="body1" fontWeight={600}>.{PLATFORM_DOMAIN}</Typography>
            </Stack>
            {slugError && <Alert severity="error" sx={{ mt: 1.5 }}>{slugError}</Alert>}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("salonProfile.subdomainRules")}
            </Typography>
            {slug !== slugSaved && !slugError && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>{t("salonProfile.subdomainChangeWarn")}</Alert>
            )}
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t("salonProfile.categories")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
              {t("salonProfile.categoriesHint")}
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              {CATEGORIES.map((c) => (
                <Chip
                  key={c.slug}
                  label={`${c.icon} ${catLabel(t, c.slug)}`}
                  onClick={() => isOwner && toggleCat(c.slug)}
                  color={categories.includes(c.slug) ? "primary" : "default"}
                  variant={categories.includes(c.slug) ? "filled" : "outlined"}
                  disabled={!isOwner}
                />
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{t("salonProfile.location")}</Typography>
            <Stack spacing={2}>
              <TextField label={t("salonProfile.city")} value={city} onChange={(e) => setCity(e.target.value)}
                disabled={!isOwner} size="small" sx={{ maxWidth: 300 }} />
              <TextField label={t("salonProfile.address")} value={address} onChange={(e) => setAddress(e.target.value)}
                disabled={!isOwner} size="small" fullWidth />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{t("salonProfile.about")}</Typography>
            <TextField label={t("salonProfile.description")} value={description}
              onChange={(e) => setDescription(e.target.value)} disabled={!isOwner}
              fullWidth multiline minRows={3} placeholder={t("salonProfile.descriptionPlaceholder")} />
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t("salonProfile.cover")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
              {t("salonProfile.coverHint")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button component="label" size="small" variant="outlined" disabled={!isOwner || uploading}>
                {uploading ? t("salonProfile.uploading") : t("salonProfile.upload")}
                <input hidden type="file" accept="image/*" onChange={onCoverPick} />
              </Button>
              {coverUrl && (
                <Button size="small" color="inherit" disabled={!isOwner} onClick={() => setCoverUrl("")}>
                  {t("salonProfile.clear")}
                </Button>
              )}
            </Stack>
            {coverUrl && (
              <Box sx={{
                mt: 1.5, height: 140, borderRadius: 2, border: "1px solid", borderColor: "divider",
                backgroundImage: `url(${mediaUrl(coverUrl)})`, backgroundSize: "cover", backgroundPosition: "center",
              }} />
            )}
          </Paper>

          <Box>
            <Button variant="contained" onClick={save} disabled={!isOwner || saving}>
              {saving ? t("salonProfile.saving") : t("salonProfile.save")}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
